import useSWR, { mutate } from "swr";
import {
  type MapPlotRequest,
  type MapPlotRequestApiResponse,
  type SavedMapPlotRequestDetail,
} from "@/features/map/types";
import { useApiClient } from "@/hooks/useApiClient";
import { type AxiosError, isAxiosError } from "axios";
import { useCallback, useState } from "react";
import { type GridSelectionModel } from "@mui/x-data-grid-pro";

export const MAP_PLOT_REQUEST_ENDPOINT = "/map/plot-request";
export const MAP_PLOT_REQUEST_DETAILS_ENDPOINT = "/map/plot-request-details";

export const useMapPlotRequest = (
  shouldFetch: boolean
): {
  mapPlotRequestData: MapPlotRequest[] | undefined;
  isLoading: boolean;
  isError: boolean;
  refetch: () => Promise<MapPlotRequest[]>;
  fetchPlotDetails: (
    requestIds: GridSelectionModel
  ) => Promise<SavedMapPlotRequestDetail[]>;
} => {
  const { apiClient } = useApiClient();
  // 手動でローディング状態を管理
  const [isManualLoading, setIsManualLoading] = useState(false);

  const fetcher = async (url: string): Promise<MapPlotRequest[]> => {
    const response = await apiClient.get<MapPlotRequestApiResponse[]>(url);

    // APIレスポンスを型定義に合わせて変換
    return response.data.map((item) => ({
      id: item.id,
      name: item.name,
      count: item.count,
      uploadFileUri: item.uploadFileUri,
      createdAt: item.createdAt,
      updatedAt: item.updatedAt,
      tags: item.tags,
    }));
  };

  const { data, error } = useSWR<MapPlotRequest[], AxiosError>(
    shouldFetch ? MAP_PLOT_REQUEST_ENDPOINT : null,
    fetcher,
    {
      revalidateOnFocus: false,
      revalidateIfStale: false,
      revalidateOnReconnect: false,
      suspense: false,
      shouldRetryOnError: false,
    }
  );

  // refetch関数をuseCallbackでメモ化して、不要な再レンダリングを防ぐ
  const refetch = useCallback(async (): Promise<MapPlotRequest[]> => {
    try {
      setIsManualLoading(true);
      const result = await fetcher(MAP_PLOT_REQUEST_ENDPOINT);
      // 第3引数をfalseにして、再検証を防ぐ
      void mutate(MAP_PLOT_REQUEST_ENDPOINT, result, false);
      return result;
    } catch (e) {
      console.error("地図プロット情報の取得に失敗しました:", e);
      void mutate(MAP_PLOT_REQUEST_ENDPOINT, undefined, false);
      throw e;
    } finally {
      setIsManualLoading(false);
    }
  }, []);

  const fetchPlotDetails = useCallback(
    async (
      requestIds: GridSelectionModel
    ): Promise<SavedMapPlotRequestDetail[]> => {
      try {
        setIsManualLoading(true);
        // 複数IDをクエリパラメータとして送信
        const query = requestIds.map((id) => `id=${id}`).join("&");
        const response = await apiClient.get<SavedMapPlotRequestDetail[]>(
          `${MAP_PLOT_REQUEST_DETAILS_ENDPOINT}?${query}`
        );
        return response.data;
      } catch (e: unknown) {
        // エラーメッセージをより詳細に
        if (isAxiosError(e)) {
          if (e.response?.status === 404) {
            console.error(
              `指定されたIDのプロットデータが見つかりませんでした (IDs: ${requestIds.join(
                ", "
              )})`,
              e
            );
          } else {
            console.error(
              `地図プロット詳細の取得に失敗しました (Status: ${String(
                e.response?.status
              )}, ID: ${requestIds.join(", ")})`,
              e
            );
          }
        } else {
          console.error(
            `地図プロット詳細の取得に失敗しました (IDs: ${requestIds.join(
              ", "
            )})`,
            e
          );
        }
        // 元のエラーをそのままスロー（上位コンポーネントでステータスを確認できるように）
        throw e;
      } finally {
        setIsManualLoading(false);
      }
    },
    [apiClient]
  );

  // SWRのローディング状態と手動のローディング状態を組み合わせる
  const isLoading = (shouldFetch && !error && !data) || isManualLoading;

  return {
    mapPlotRequestData: data,
    isLoading,
    isError: !!error,
    refetch,
    fetchPlotDetails,
  };
};
